import { useEffect, useMemo, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { effect } from '@preact/signals-react';
import { ANYONE, ANYONE_ID } from 'Calendar/anyone';
import StaffAvailablePicker from 'widgets/Staff/StaffAvailablePicker';
import TimeSlotAvailablePicker from 'widgets/Staff/TimeSlotAvailablePicker';
import aptSelectors from 'services/selectors/apt';
import shopSelectors from 'services/selectors/shop';
import aptActions from 'services/actions/apt';
import apis from 'services/apis';
import { IQuickBookingServiceItem } from 'services/types/quickBooking';
import { fetchBookingContext, isBookingEnhance } from 'services/booking-context';
import { IStaffUI } from 'types/staff';
import { IDatePickerValue } from 'components/DatePicker';
import store from 'store';
import { useAppDispatch } from 'store/hooks';
import useCallerId from 'widgets/CallerId/useCallerId';
import { callerSelectResult } from 'Calendar/caller-booking-selection';
import { dateParamSignal } from 'CalendarFilter/DatePicker';
import storage from 'utils/sessionStorage';
import toast from 'utils/toast';
import { isBookableStaff } from 'utils/staff';
import uuid from 'utils/uuid';
import moment from 'utils/moment';
import { ICallerCallItem, ICallerHistoryEntry } from '../types';
import { callerBookingOutcome } from '../caller-booking-outcome';
import { createCallerBookingState } from './caller-booking-state';
import { buildCallerBookingItem } from './caller-booking-payload';
import { confirmSameDayBooking } from 'widgets/SameDayBookingAlert/confirm-same-day-booking';

type Options = {
  // Called once the server accepted the booking and the composer was cleared.
  onBooked?: () => void;
};

// Drives the inline "Booking — Order" composer. Its state is private to this
// composer (see caller-booking-state) and the customer/caller id of the booking
// always come from `item`, so nothing else on the page can change who is booked.
// It still reuses the staff/time pickers and the quickBooking saga.
export const useBookingComposer = (item: ICallerCallItem, { onBooked }: Options = {}) => {
  const dispatch = useAppDispatch();
  const callerSvc = useCallerId();
  const services = aptSelectors.quickBooking.services();
  const allServices = shopSelectors.allServices();
  const state = useMemo(createCallerBookingState, []);
  const [staff, setStaff] = useState<IStaffUI>(ANYONE);
  // Guards the Re Book buttons while the staff-availability check is in flight,
  // so a double click cannot open two time pickers.
  const [rebookLoading, setRebookLoading] = useState(false);
  // True from BOOK until the saga reports the outcome — blocks a double submit.
  const [submitting, setSubmitting] = useState(false);
  // Bumped each time the next step is the service picker (calendar range applied,
  // or staff → time completed) so the composer UI opens it.
  const [servicesPromptToken, setServicesPromptToken] = useState(0);
  // Set once the user toggles the Request Staff checkbox themselves. A ref, not
  // state: the async pickers and the calendar effect read it from closures that
  // would otherwise capture a stale value.
  const requestStaffTouched = useRef(false);
  // requestId of the BOOK press still waiting for its outcome; anything else is ignored.
  const pendingRequestId = useRef<string | null>(null);
  const onBookedRef = useRef(onBooked);
  onBookedRef.current = onBooked;

  // Single place that decides requestStaff whenever the staff changes. "Anyone"
  // cannot be requested, so it forces the flag off and forgets any manual choice.
  const applyStaffSelection = (picked: IStaffUI) => {
    state.staffId.value = picked.id;
    if (picked.id === ANYONE_ID) {
      state.requestStaff.value = false;
      requestStaffTouched.current = false;
    } else if (!requestStaffTouched.current) {
      state.requestStaff.value = true;
    }
    setStaff(picked);
  };

  // Empty booking order for the active caller.
  const resetOrder = () => {
    state.date.value = dateParamSignal.value?.clone() || moment();
    state.startTime.value = '';
    state.services.value = [];
    state.note.value = '';
    state.duration.value = 60;
    callerSelectResult.value = null; // drop any calendar range picked for a prior caller
    requestStaffTouched.current = false; // a new caller forgets the previous one's manual choice
    applyStaffSelection(ANYONE);
  };

  useEffect(() => {
    resetOrder();
    setSubmitting(false);
    pendingRequestId.current = null; // a result for the previous caller must not act here
    if (!services.length) {
      const shopId = storage.shopId.get();
      if (shopId) dispatch(aptActions.quickBooking.getServices.fetch(shopId));
    }
  }, [item.id]); // re-seed whenever a different caller becomes active

  // Booking result for THIS caller: clear the booked customer only on success,
  // keep the form on failure so the staff can retry.
  useEffect(() => {
    const dispose = effect(() => {
      const outcome = callerBookingOutcome.value;
      if (!outcome || outcome.requestId !== pendingRequestId.current) return;
      pendingRequestId.current = null;
      callerBookingOutcome.value = null;
      setSubmitting(false);
      if (outcome.cancelled) {
        // The request may still have gone through — retrying blindly could double book.
        toast.error('Booking status unknown. Please check the calendar before booking again.');
        return;
      }
      if (!outcome.ok) return;
      resetOrder();
      dispatch(aptActions.clearCustomerHistory());
      onBookedRef.current?.();
    });
    return dispose;
  }, [item.id]);

  // Apply a range the user picked directly on the calendar → staff + time + duration.
  useEffect(() => {
    const dispose = effect(() => {
      const res = callerSelectResult.value;
      if (!res) return;
      state.date.value = res.start.clone();
      state.startTime.value = res.start.format('hh:mm A');
      state.duration.value = res.duration;
      // Column ids come from staffsAvailable (useCalendarFilter), which refreshes per
      // date while the full roster does not — search it first and only then fall back,
      // so a real staff column is never downgraded to Anyone in the submitted payload.
      const shopState = store.getState().shop;
      const list = [
        ...((shopState?.staffsAvailable || []) as IStaffUI[]),
        ...((shopState?.staffs || []) as IStaffUI[]),
      ];
      applyStaffSelection(list.find((s) => s.id === res.staffId) || ANYONE);
      // Picking the range on the calendar is the "when"; services are the next
      // step, so hand the token to the composer UI to open the picker.
      setServicesPromptToken(res.token);
    });
    return dispose;
  }, []);

  // Keep the composer date in sync with the calendar's selected date — changing the
  // day on the calendar reflects into the booking order.
  useEffect(() => {
    const dispose = effect(() => {
      const calendarDate = dateParamSignal.value;
      if (calendarDate) state.date.value = calendarDate.clone();
    });
    return dispose;
  }, []);

  // Opens the time picker for the selected staff. Resolves true once a slot is
  // chosen; closing the picker leaves the promise unsettled, so callers must never
  // hold a loading guard across it.
  const chooseTime = async () => {
    const staffId = state.staffId.value || ANYONE_ID;
    const slot = await TimeSlotAvailablePicker.get(staffId, state.date.value);
    if (!slot) return false;
    state.startTime.value = slot.format('hh:mm A');
    state.date.value = slot;
    return true;
  };

  // Guided flow: staff → time → services. Each step opens only after the previous
  // one was completed; closing a picker ends the flow there.
  const pickStaff = async () => {
    const picked = await StaffAvailablePicker.get();
    if (!picked) return;
    applyStaffSelection(picked);
    if (!(await chooseTime())) return;
    // Date.now() never collides with the calendar's small counter tokens, so the
    // composer effect always sees a new value and opens the service picker.
    if (services.length) setServicesPromptToken(Date.now());
  };

  // Start time chip (and Re Book): only the time — services are untouched.
  const pickTime = () => {
    chooseTime();
  };

  // Manual checkbox toggle — remembered so picking another staff keeps the choice.
  const setRequestStaff = (checked: boolean) => {
    state.requestStaff.value = checked;
    requestStaffTouched.current = true;
  };

  const setDate = (date: IDatePickerValue) => {
    if (date) state.date.value = date;
  };
  const setDuration = (value: number) => {
    state.duration.value = value;
  };
  const setNote = (value: string) => {
    state.note.value = value;
  };
  const addService = (svc: IQuickBookingServiceItem) => {
    state.services.value = [
      ...state.services.value,
      { label: svc.serviceName, value: uuid(), id: svc.id },
    ];
  };
  const removeService = (value: string) => {
    state.services.value = state.services.value.filter((o) => o.value !== value);
  };

  // Add the service if not already chosen, else remove it (by name) — for the picker modal.
  const toggleService = (svc: IQuickBookingServiceItem) => {
    const exists = state.services.value.some((o) => o.label === svc.serviceName);
    if (exists) {
      state.services.value = state.services.value.filter((o) => o.label !== svc.serviceName);
    } else {
      addService(svc);
    }
  };

  // Re Book: replace the composer services from a visit's comma-separated services
  // string (e.g. "Gel, M/P (Mani & Pedi)"). History items carry only a name, so we
  // search the catalog by name and auto-select the matching service (with its id);
  // matched items adopt the catalog name/id so they render + toggle consistently.
  // The catalog spans both the quick list AND the full shop services so a re-book
  // resolves any service the customer previously had, not just quick ones.
  // Unmatched names stay as free tags (id '' → the payload sends id 0).
  const prefillServices = (servicesStr: string) => {
    const names = servicesStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const norm = (s: string) => s.trim().toLowerCase();
    // Unified catalog: quick services first (curated), then the full shop catalog.
    const catalog = [
      ...services.map((s) => ({ name: s.serviceName, id: s.id as string | number })),
      ...allServices.map((s) => ({ name: s.name, id: s.id as string | number })),
    ];
    state.services.value = names.map((name) => {
      const key = norm(name);
      const match =
        catalog.find((s) => norm(s.name) === key) ||
        catalog.find((s) => norm(s.name).includes(key) || key.includes(norm(s.name)));
      return { label: match?.name ?? name, value: uuid(), id: match?.id ?? '' };
    });
  };

  // Re Book: always prefill the services, then try to bring back the staff who
  // took that visit. The staff is only auto-selected when they still have free
  // time on the selected day — otherwise the user has to pick someone else
  // instead of silently booking into a full schedule.
  const rebook = async (entry: ICallerHistoryEntry) => {
    prefillServices(entry.services);
    if (!entry.staffId) return; // the old visit was booked as "Anyone" — nothing to restore

    const staffsObject = (store.getState().shop?.staffsObject || {}) as Record<string, IStaffUI>;
    const found = staffsObject[entry.staffId];
    if (!found || !isBookableStaff(found)) {
      toast.error('That staff is no longer available for booking. Please choose another staff.');
      return;
    }

    const date = state.date.value;
    let slots: string[] = [];
    setRebookLoading(true);
    try {
      const day = date?.format('YYYY-MM-DD') || '';
      if (isBookingEnhance()) {
        slots = (await fetchBookingContext(entry.staffId, day)).timeSlots;
      } else {
        const res: AxiosResponse<{ payload: { timeSlots: string[] } }> = await apis.getTimeSlot(
          storage.shopId.get(),
          day,
          entry.staffId,
        );
        slots = res?.data?.payload?.timeSlots || [];
      }
    } catch {
      toast.error('Could not check staff availability. Please try again.');
      return;
    } finally {
      setRebookLoading(false);
    }

    if (!slots.length) {
      const when = date?.format('MM-DD-YYYY') || 'that day';
      toast.error(`${found.name} is not available on ${when}. Please choose another staff.`);
      return;
    }
    applyStaffSelection(found);
    // Deliberately not awaited: the picker leaves its promise unsettled when the
    // user closes it without choosing, so awaiting here would strand the loading
    // guard and disable Re Book for good. Same fire-and-forget as the Start time chip.
    pickTime();
  };

  const canBook = !submitting && Boolean(state.startTime.value) && state.services.value.length > 0;

  const onSms = async () => {
    await callerSvc.sms(item.id, { id: item.customerId ?? null, name: item.name, phone: item.phone });
    // SMS handled this caller → refresh the pending list, close the panel if empty.
    dispatch(aptActions.getPendingCallers.fetch({ closeIfEmpty: true }));
  };

  const submit = async () => {
    if (!canBook) return;
    // Who is booked comes from the active caller only — never from shared state.
    const booking = buildCallerBookingItem({
      customerId: item.customerId,
      callerId: item.id,
      staffId: state.staffId.value || ANYONE_ID,
      requestStaff: state.requestStaff.value,
      date: state.date.value,
      startTime: state.startTime.value,
      duration: state.duration.value,
      note: state.note.value,
      services: state.services.value,
    });
    if (!booking) {
      toast.error('This caller has no customer to book. Please go back and open the caller again.');
      return;
    }
    // Customer already booked that day → warn + staff PIN, sent along so the server knows who booked.
    const sameDay = await confirmSameDayBooking({ customerId: booking.customerId, startTime: booking.startTime });
    if (sameDay.status === 'cancel') return;
    const requestId = uuid();
    pendingRequestId.current = requestId;
    setSubmitting(true);
    const items = [sameDay.actor ? { ...booking, ...sameDay.actor } : booking];
    dispatch(aptActions.quickBooking.doQuickBooking({ items, callerRequestId: requestId }));
  };

  return {
    state,
    services,
    staff,
    pickStaff,
    pickTime,
    setRequestStaff,
    setDate,
    setDuration,
    setNote,
    addService,
    removeService,
    toggleService,
    rebook,
    rebookLoading,
    servicesPromptToken,
    canBook,
    submitting,
    onSms,
    submit,
  };
};

export default useBookingComposer;
